import { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import GRNSelectiveDeletePanel from '@/components/hq/GRNSelectiveDeletePanel';
import AutoBackupPanel from '@/components/hq/AutoBackupPanel';
import { 
  RefreshCw, Download, Upload, Trash2, Database, AlertTriangle,
  Shield, FileJson, FolderArchive, CheckCircle, XCircle, Info,
  HardDrive, Settings2, Key
} from 'lucide-react';

export default function SystemSettings() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [resetKey, setResetKey] = useState('');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetPreview, setResetPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreKey, setRestoreKey] = useState('');
  const [clearExisting, setClearExisting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/system/collections/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load collection statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleDownloadBackup = async () => {
    try {
      setDownloading(true);
      const response = await api.get('/system/backup', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'systore_backup.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully');
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  const handleResetPreview = async () => {
    if (!resetKey) {
      toast.error('Please enter the reset key');
      return;
    }
    
    try {
      setSubmitting(true);
      const res = await api.post('/system/reset', null, {
        params: { reset_key: resetKey, confirm: false }
      });
      setResetPreview(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Invalid reset key');
      } else {
        toast.error('Failed to preview reset');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetConfirm = async () => {
    if (!resetConfirm) {
      toast.error('Please check the confirmation box');
      return;
    }
    
    try {
      setSubmitting(true);
      const res = await api.post('/system/reset', null, {
        params: { reset_key: resetKey, confirm: true }
      });
      
      toast.success('Project reset completed successfully');
      setShowResetDialog(false);
      setResetKey('');
      setResetConfirm(false);
      setResetPreview(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRestoreFile(file);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file');
      return;
    }
    
    if (!restoreKey) {
      toast.error('Please enter the restore key');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Read file content
      const fileContent = await restoreFile.text();
      let backupData;
      
      try {
        backupData = JSON.parse(fileContent);
      } catch {
        toast.error('Invalid JSON file format');
        return;
      }
      
      const res = await api.post('/system/restore', null, {
        params: {
          restore_key: restoreKey,
          backup_json: JSON.stringify(backupData),
          clear_existing: clearExisting
        }
      });
      
      toast.success('Database restored successfully');
      setShowRestoreDialog(false);
      setRestoreFile(null);
      setRestoreKey('');
      setClearExisting(false);
      fetchStats();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Invalid restore key');
      } else {
        toast.error(error.response?.data?.detail || 'Restore failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="system-settings-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <p className="text-gray-600">Database management, backup and restore</p>
        </div>
        <Button onClick={fetchStats} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Warning Banner */}
      <Alert className="border-red-200 bg-red-50">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <AlertTitle className="text-red-800">Danger Zone</AlertTitle>
        <AlertDescription className="text-red-700">
          The operations on this page can permanently delete data. Always create a backup before performing any reset operation.
        </AlertDescription>
      </Alert>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Documents</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(stats?.total_documents)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Transaction Collections</p>
                <p className="text-2xl font-bold text-orange-600">
                  {Object.keys(stats?.transaction_data || {}).filter(k => stats.transaction_data[k] > 0).length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-orange-100">
                <HardDrive className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Organization Collections</p>
                <p className="text-2xl font-bold text-green-600">
                  {Object.keys(stats?.organization_data || {}).filter(k => stats.organization_data[k] > 0).length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors cursor-pointer" onClick={handleDownloadBackup}>
          <CardContent className="pt-6 text-center">
            <div className="p-4 rounded-full bg-blue-100 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              {downloading ? (
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
              ) : (
                <Download className="w-8 h-8 text-blue-600" />
              )}
            </div>
            <h3 className="font-semibold text-lg mb-2">Download Backup</h3>
            <p className="text-sm text-gray-600">
              Download full database backup as ZIP file with setup guide
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200 hover:border-green-400 transition-colors cursor-pointer" onClick={() => setShowRestoreDialog(true)}>
          <CardContent className="pt-6 text-center">
            <div className="p-4 rounded-full bg-green-100 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Upload className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Restore Backup</h3>
            <p className="text-sm text-gray-600">
              Restore database from a backup JSON file
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-red-200 hover:border-red-400 transition-colors cursor-pointer" onClick={() => setShowResetDialog(true)}>
          <CardContent className="pt-6 text-center">
            <div className="p-4 rounded-full bg-red-100 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Reset Project</h3>
            <p className="text-sm text-gray-600">
              Delete all transaction data (requires security key)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Collection Statistics */}
      <Tabs defaultValue="transaction">
        <TabsList>
          <TabsTrigger value="transaction">
            <HardDrive className="w-4 h-4 mr-2" />
            Transaction Data
          </TabsTrigger>
          <TabsTrigger value="organization">
            <Shield className="w-4 h-4 mr-2" />
            Organization Data
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transaction" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Transaction Collections</CardTitle>
              <CardDescription>These collections will be deleted during project reset</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(stats?.transaction_data || {}).map(([name, count]) => (
                  <div key={name} className={`p-3 rounded border ${count > 0 ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="text-xs text-gray-500 truncate">{name}</div>
                    <div className={`font-semibold ${count > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                      {formatNumber(count)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="organization" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Organization Collections</CardTitle>
              <CardDescription>These collections will be preserved during project reset</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(stats?.organization_data || {}).map(([name, count]) => (
                  <div key={name} className={`p-3 rounded border ${count > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="text-xs text-gray-500 truncate">{name}</div>
                    <div className={`font-semibold ${count > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {formatNumber(count)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Auto Backup - runs daily at midnight IST, keeps last 5 on Bunny CDN */}
      <AutoBackupPanel />

      {/* Selective GRN Deletion - full reversal of stock + ledger, uses master Reset Key */}
      <GRNSelectiveDeletePanel />

      {/* Setup Guide Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Setup Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Environment Variables</h4>
            <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
{`# Backend (.env)
MONGO_URL=mongodb://localhost:27017
DB_NAME=systore
JWT_SECRET_KEY=your-secret-key

# Frontend (.env)
REACT_APP_BACKEND_URL=http://localhost:8001`}
            </pre>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Backup Contents</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <code>backup_data.json</code> - Complete database export</li>
              <li>• <code>env_template.txt</code> - Environment variable template</li>
              <li>• <code>SETUP_GUIDE.md</code> - Installation and restore guide</li>
              <li>• <code>collections/*.json</code> - Individual collection exports</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Project Reset
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all transaction data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">
                Make sure you have downloaded a backup before proceeding!
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="reset_key" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Security Key *
              </Label>
              <Input
                id="reset_key"
                type="password"
                placeholder="Enter the security key"
                value={resetKey}
                onChange={(e) => setResetKey(e.target.value)}
              />
            </div>

            {!resetPreview ? (
              <Button onClick={handleResetPreview} disabled={submitting} className="w-full">
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                Preview Changes
              </Button>
            ) : (
              <>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2">Collections to be deleted:</h4>
                  <div className="grid grid-cols-2 gap-1 text-sm">
                    {Object.entries(resetPreview.collections_to_delete || {}).map(([name, count]) => (
                      <div key={name} className="flex justify-between">
                        <span className="text-gray-600">{name}</span>
                        <span className="text-red-600 font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">Collections to be kept:</h4>
                  <div className="flex flex-wrap gap-2">
                    {resetPreview.collections_to_keep?.map((name) => (
                      <Badge key={name} variant="outline" className="bg-green-100">
                        {name}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="confirm" 
                    checked={resetConfirm}
                    onCheckedChange={setResetConfirm}
                  />
                  <label htmlFor="confirm" className="text-sm text-gray-700">
                    I understand this action is irreversible and have a backup
                  </label>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowResetDialog(false);
              setResetKey('');
              setResetConfirm(false);
              setResetPreview(null);
            }}>
              Cancel
            </Button>
            {resetPreview && (
              <Button 
                variant="destructive" 
                onClick={handleResetConfirm} 
                disabled={submitting || !resetConfirm}
              >
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Reset Project
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Restore Database
            </DialogTitle>
            <DialogDescription>
              Restore database from a backup JSON file. This requires the security key.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="backup_file">Backup File *</Label>
              <Input
                id="backup_file"
                type="file"
                accept=".json"
                onChange={handleFileSelect}
              />
              {restoreFile && (
                <p className="text-sm text-gray-500">
                  Selected: {restoreFile.name} ({(restoreFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="restore_key" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Security Key *
              </Label>
              <Input
                id="restore_key"
                type="password"
                placeholder="Enter the security key"
                value={restoreKey}
                onChange={(e) => setRestoreKey(e.target.value)}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="clear_existing" 
                checked={clearExisting}
                onCheckedChange={setClearExisting}
              />
              <label htmlFor="clear_existing" className="text-sm text-gray-700">
                Clear existing data before restore (recommended for full restore)
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRestoreDialog(false);
              setRestoreFile(null);
              setRestoreKey('');
              setClearExisting(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleRestore} disabled={submitting}>
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
